// BlockMenu — popover for inserting blocks or whole templates.
//
// Layout: a left-rail of category tabs (Text / Media / Embed / Layout +
// Templates) and a content area showing the items in the current
// category. A search field filters across all categories.
//
// Templates are pre-filled multi-block scaffolds keyed by name. When
// picked, BlockEditor inserts the entire block array rather than a single
// block. To distinguish, the parent's `onPick` accepts BlockType for
// individual blocks; we expose a separate `onPickTemplate` for templates.

import { useEffect, useMemo, useRef, useState } from "react";
import { BLOCK_TYPES, type Block, type BlockType, makeBlock, newId } from "../../lib/blocks";
import { t as theme } from "./theme";
import { useT, type T } from "../../lib/i18n";

interface BlockItemDef {
  type: BlockType;
  label: string;
  desc: string;
  glyph: string;
}

const textBlocks = (t: T): BlockItemDef[] => [
  { type: "heading",   label: t.blockTypeHeading,   desc: t.blockDescHeading,   glyph: "H" },
  { type: "paragraph", label: t.blockTypeParagraph, desc: t.blockDescParagraph, glyph: "T" },
  { type: "quote",     label: t.blockTypeQuote,     desc: t.blockDescQuote,     glyph: "”" },
  { type: "list",      label: t.blockTypeList,      desc: t.blockDescList,      glyph: "≡" },
  { type: "caption",   label: t.blockTypeCaption,   desc: t.blockDescCaption,   glyph: "—" },
];

const mediaBlocks = (t: T): BlockItemDef[] => [
  { type: "image",       label: t.blockTypeImage,      desc: t.blockDescImage,      glyph: "▢" },
  { type: "gallery",     label: t.blockTypeGallery,    desc: t.blockDescGallery,    glyph: "▦" },
  { type: "video_embed", label: t.blockTypeVideoEmbed, desc: t.blockDescVideoEmbed, glyph: "▶" },
  { type: "video_file",  label: t.blockTypeVideoFile,  desc: t.blockDescVideoFile,  glyph: "◉" },
  { type: "audio_embed", label: t.blockTypeAudioEmbed, desc: t.blockDescAudioEmbed, glyph: "♪" },
  { type: "audio_file",  label: t.blockTypeAudioFile,  desc: t.blockDescAudioFile,  glyph: "♫" },
];

const embedBlocks = (t: T): BlockItemDef[] => [
  { type: "link_card", label: t.blockTypeLinkCard, desc: t.blockDescLinkCard, glyph: "⌘" },
  { type: "embed",     label: t.blockTypeEmbed,    desc: t.blockDescEmbed,    glyph: "</>" },
];

const layoutBlocks = (t: T): BlockItemDef[] => [
  { type: "divider", label: t.blockTypeDivider, desc: t.blockDescDivider, glyph: "•" },
  { type: "spacer",  label: t.blockTypeSpacer,  desc: t.blockDescSpacer,  glyph: "↕" },
  { type: "cta",     label: t.blockTypeCta,     desc: t.blockDescCta,     glyph: "→" },
  { type: "hero",    label: t.blockTypeHero,    desc: t.blockDescHero,    glyph: "★" },
];

const allBlocks = (t: T): BlockItemDef[] => [
  ...textBlocks(t), ...mediaBlocks(t), ...embedBlocks(t), ...layoutBlocks(t),
];

// Templates — fully-formed block arrays with placeholder text. Items use
// makeBlock() then patch fields, so we stay in sync with schema defaults.

export interface TemplateDef {
  key: string;
  label: string;
  desc: string;
  glyph: string;
  build: () => Block[];
}

export const TEMPLATES: TemplateDef[] = [
  {
    key: "photo_essay",
    label: "Photo Essay",
    desc: "Hero image + intro + 3-col gallery + caption",
    glyph: "❖",
    build: () => {
      const hero = makeBlock("hero") as Block & { type: "hero" };
      hero.title = "Working title";
      hero.subtitle = "A short, evocative subtitle.";
      const para = makeBlock("paragraph") as Block & { type: "paragraph" };
      para.html = "Open the essay with a few sentences that frame the project.";
      const gal = makeBlock("gallery") as Block & { type: "gallery" };
      gal.layout = "grid-3";
      gal.items = [];
      const cap = makeBlock("caption") as Block & { type: "caption" };
      cap.text = "Caption credit — photographer name.";
      return [withId(hero), withId(para), withId(gal), withId(cap)];
    },
  },
  {
    key: "video_review",
    label: "Video Review",
    desc: "Hero video + heading + paragraph + CTA",
    glyph: "▶",
    build: () => {
      const hero = makeBlock("hero") as Block & { type: "hero" };
      hero.mediaKind = "video_embed";
      hero.title = "Working title";
      const h = makeBlock("heading") as Block & { type: "heading" };
      h.level = 2; h.text = "What this is";
      const para = makeBlock("paragraph") as Block & { type: "paragraph" };
      para.html = "Describe the piece, the director, the why.";
      const cta = makeBlock("cta") as Block & { type: "cta" };
      cta.label = "Watch on Vimeo"; cta.url = "";
      return [withId(hero), withId(h), withId(para), withId(cta)];
    },
  },
  {
    key: "music_drop",
    label: "Music Drop",
    desc: "Audio embed + heading + paragraph + CTA",
    glyph: "♫",
    build: () => {
      const audio = makeBlock("audio_embed") as Block & { type: "audio_embed" };
      audio.url = ""; audio.style = "standard";
      const h = makeBlock("heading") as Block & { type: "heading" };
      h.level = 2; h.text = "Track name";
      const para = makeBlock("paragraph") as Block & { type: "paragraph" };
      para.html = "Liner notes for the release.";
      const cta = makeBlock("cta") as Block & { type: "cta" };
      cta.label = "Listen everywhere"; cta.url = "";
      return [withId(audio), withId(h), withId(para), withId(cta)];
    },
  },
];

function withId<T extends Block>(b: T): T {
  return { ...b, id: newId() } as T;
}

type Category = "all" | "text" | "media" | "embed" | "layout" | "templates";

const categories = (t: T): Array<{ key: Category; label: string }> => [
  { key: "all",       label: t.blockCategoryAll },
  { key: "text",      label: t.blockCategoryText },
  { key: "media",     label: t.blockCategoryMedia },
  { key: "embed",     label: t.blockCategoryEmbed },
  { key: "layout",    label: t.blockCategoryLayout },
  { key: "templates", label: t.blockCategoryTemplates },
];

export interface BlockMenuProps {
  onPick: (type: BlockType) => void;
  onPickTemplate?: (blocks: Block[]) => void;
  onClose: () => void;
}

export function BlockMenu({ onPick, onPickTemplate, onClose }: BlockMenuProps) {
  const t = useT();
  const TEXT_BLOCKS = textBlocks(t);
  const MEDIA_BLOCKS = mediaBlocks(t);
  const EMBED_BLOCKS = embedBlocks(t);
  const LAYOUT_BLOCKS = layoutBlocks(t);
  const ALL_BLOCKS = allBlocks(t);
  const CATEGORIES = categories(t);
  const ref = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState<Category>("all");

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (!ref.current) return;
      if (!ref.current.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    const tm = setTimeout(() => document.addEventListener("mousedown", onDown), 0);
    document.addEventListener("keydown", onKey);
    return () => {
      clearTimeout(tm);
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  if (BLOCK_TYPES.length !== ALL_BLOCKS.length) {
    console.warn("BlockMenu: ITEMS out of sync with BLOCK_TYPES");
  }

  const blocksForCategory = useMemo<BlockItemDef[]>(() => {
    switch (category) {
      case "text": return TEXT_BLOCKS;
      case "media": return MEDIA_BLOCKS;
      case "embed": return EMBED_BLOCKS;
      case "layout": return LAYOUT_BLOCKS;
      case "templates": return [];
      default: return ALL_BLOCKS;
    }
  }, [category]);

  const filteredBlocks = useMemo(() => {
    const q = query.trim().toLowerCase();
    const src = category === "templates" ? [] : blocksForCategory;
    if (!q) return src;
    return src.filter(
      (it) =>
        it.label.toLowerCase().includes(q) ||
        it.type.toLowerCase().includes(q) ||
        it.desc.toLowerCase().includes(q),
    );
  }, [blocksForCategory, category, query]);

  const filteredTemplates = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (category !== "templates" && category !== "all" && !q) return [];
    if (!q && category === "all") return [];
    if (!q) return TEMPLATES;
    return TEMPLATES.filter(
      (tpl) => tpl.label.toLowerCase().includes(q) || tpl.desc.toLowerCase().includes(q),
    );
  }, [category, query]);

  return (
    <div
      ref={ref}
      role="menu"
      style={{
        marginTop: 6,
        width: 560,
        background: theme.card,
        border: `1px solid ${theme.border}`,
        borderRadius: 10,
        boxShadow: "0 18px 40px rgba(0,0,0,0.45)",
        padding: 10,
        display: "flex",
        flexDirection: "column",
        gap: 8,
        color: theme.text,
        fontFamily: theme.sans,
      }}
    >
      <input
        type="text"
        autoFocus
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={t.blockMenuSearch}
        style={{
          width: "100%", boxSizing: "border-box",
          padding: "8px 10px",
          background: theme.bg,
          border: `1px solid ${theme.border}`,
          borderRadius: 6,
          color: theme.text,
          fontSize: 13,
          outline: "none",
        }}
      />
      <div style={{ display: "flex", gap: 4 }}>
        {CATEGORIES.map((c) => {
          const active = category === c.key;
          return (
            <button
              key={c.key}
              type="button"
              onClick={() => setCategory(c.key)}
              style={{
                padding: "4px 11px",
                fontSize: 11,
                fontWeight: active ? 600 : 500,
                borderRadius: 100,
                border: `1px solid ${active ? theme.accentStrong : "transparent"}`,
                background: active ? theme.accentSoft : "transparent",
                color: active ? theme.accent : theme.text3,
                cursor: "pointer",
              }}
            >
              {c.label}
            </button>
          );
        })}
      </div>

      {/* Templates */}
      {filteredTemplates.length > 0 && (
        <>
          {category === "all" && (
            <div style={{ fontSize: 10, color: theme.text3, textTransform: "uppercase", letterSpacing: "0.08em", padding: "4px 2px 2px" }}>
              {t.blockCategoryTemplates}
            </div>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6 }}>
            {filteredTemplates.map((tmpl) => (
              <button
                key={tmpl.key}
                type="button"
                title={tmpl.desc}
                onClick={() => {
                  if (onPickTemplate) onPickTemplate(tmpl.build());
                }}
                style={tmplBtn}
                onMouseEnter={(e) => {
                  const b = e.currentTarget as HTMLButtonElement;
                  b.style.borderColor = theme.accentStrong;
                  b.style.background = theme.accentSoft;
                }}
                onMouseLeave={(e) => {
                  const b = e.currentTarget as HTMLButtonElement;
                  b.style.borderColor = theme.border;
                  b.style.background = "transparent";
                }}
              >
                <span style={{ fontFamily: theme.serif, fontSize: 18, lineHeight: 1, color: theme.accent }}>{tmpl.glyph}</span>
                <span style={{ fontSize: 12, fontWeight: 600, color: theme.text }}>{tmpl.label}</span>
                <span style={{ fontSize: 11, color: theme.text3, lineHeight: 1.3 }}>{tmpl.desc}</span>
              </button>
            ))}
          </div>
        </>
      )}

      {/* Blocks */}
      {filteredBlocks.length > 0 && (
        <>
          {category === "all" && filteredTemplates.length > 0 && (
            <div style={{ fontSize: 10, color: theme.text3, textTransform: "uppercase", letterSpacing: "0.08em", padding: "8px 2px 2px" }}>
              {t.blockMenuHeadingLabel}
            </div>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 6 }}>
            {filteredBlocks.map((it) => (
              <button
                key={it.type}
                type="button"
                onClick={() => onPick(it.type)}
                title={it.desc}
                style={blockBtn}
                onMouseEnter={(e) => {
                  const b = e.currentTarget as HTMLButtonElement;
                  b.style.borderColor = theme.accentStrong;
                  b.style.background = theme.accentSoft;
                }}
                onMouseLeave={(e) => {
                  const b = e.currentTarget as HTMLButtonElement;
                  b.style.borderColor = theme.border;
                  b.style.background = "transparent";
                }}
              >
                <span style={{ fontFamily: theme.serif, fontSize: 18, lineHeight: 1, color: theme.accent }}>{it.glyph}</span>
                <span style={{ fontSize: 12, fontWeight: 600, color: theme.text }}>{it.label}</span>
                <span style={{ fontSize: 11, color: theme.text3, lineHeight: 1.3 }}>{it.desc}</span>
              </button>
            ))}
          </div>
        </>
      )}

      {filteredBlocks.length === 0 && filteredTemplates.length === 0 && (
        <div style={{ padding: 16, textAlign: "center", color: theme.text3, fontSize: 12 }}>
          {t.blockMenuNoMatches}
        </div>
      )}
    </div>
  );
}

const blockBtn: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: 4,
  padding: "10px 10px",
  background: "transparent",
  border: `1px solid ${theme.border}`,
  borderRadius: 8,
  textAlign: "left",
  cursor: "pointer",
  transition: theme.transition,
};
const tmplBtn: React.CSSProperties = { ...blockBtn };
